import type { Agent, Architecture } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import { OperatingSystem } from '@studio/react-utils/user-agent';
import type { T_NotNil } from '@tryiris-ai/js-types';
import QuickLRU from 'quick-lru';
import type { StudioError } from '#~/error/index.js';

/** Minimum amount of time to wait before reporting a connection error (`5 seconds`). */
export const ConnectionTimeout = 5e3;

/** Error IDs. */
export const ErrorIds = Object.freeze({
  ConnectionFailed: 'connection.failed'
} satisfies Record<T_NotNil<StudioError['id']>, string>);

/** Language constants. */
export const Lang = Object.freeze({
  operatingSystem: {
    [OperatingSystem.Linux]: 'Linux',
    [OperatingSystem.Windows]: 'Windows',
    [OperatingSystem.macOS]: 'macOS'
  } satisfies Record<OperatingSystem, string>,

  status: {
    'idle': 'Idle',
    'booting': 'Booting',
    'offline': 'Offline',
    'offline-embedded': 'Offline',
    'online': 'Online'
  } satisfies Record<Agent['status'], string>
});

/** @internal */
export const locationCache = new QuickLRU<Device['id'], Agent['id']>({
  maxSize: 100
});

/** @internal */
// eslint-disable-next-line sort-exports/sort-exports
export const Blueprint = Object.freeze({
  [OperatingSystem.Linux]: { x86: `${Lang.operatingSystem[OperatingSystem.Linux]} - x86` },
  [OperatingSystem.Windows]: {
    win64: `${Lang.operatingSystem[OperatingSystem.Windows]} - x64`
  },
  [OperatingSystem.macOS]: {
    arm64: `${Lang.operatingSystem[OperatingSystem.macOS]} - Apple Silicon`
  }
} satisfies Record<OperatingSystem, { [key in Architecture]?: string }>);

/** @internal */
export const HeartbeatInterval = 2.5e4;
